import { logger } from 'modules/logger';
import { IBrowserClient } from '@splitsoftware/splitio/types/splitio';

import { getFactory } from './factory';
import { makeGetTreatmentMockable } from './mocks';
import { env } from 'config/env';

export type StatefulSplitClientState = 'pending' | 'ready' | 'timeout';

export class StatefulSplitClient {
  splitClient: IBrowserClient;
  state: StatefulSplitClientState;
  stateChangeHandlers: Set<(arg1: StatefulSplitClientState) => void>;

  constructor(client: IBrowserClient) {
    this.splitClient = client;
    this.state = 'pending';
    logger.info('Split Client pending');
    this.stateChangeHandlers = new Set();

    // Use client promise - that way we do not miss the event
    client
      .ready()
      .then(() => {
        this.state = 'ready';
        logger.info('Split Client ready');
        this.stateChangeHandlers.forEach((handler) => handler(this.state));
      })
      .catch(() => {
        this.state = 'timeout';
        logger.info('Split Client timed out');
        this.stateChangeHandlers.forEach((handler) => handler(this.state));
      });
  }

  onStateChange(callback: (arg1: StatefulSplitClientState) => void) {
    this.stateChangeHandlers.add(callback);
  }

  offStateChange(callback: (arg1: StatefulSplitClientState) => void) {
    this.stateChangeHandlers.delete(callback);
  }
}

export const statefulSplitClientMap: Map<string, StatefulSplitClient> = new Map();

export function getClientWithState(email: string): StatefulSplitClient {
  const existingClient = statefulSplitClientMap.get(email);
  if (existingClient != null) {
    return existingClient;
  }

  // Destroying the client will destroy the factory
  statefulSplitClientMap.forEach((statefulClient) => statefulClient.splitClient.destroy());
  statefulSplitClientMap.clear();

  logger.info('Initializing Split.io SDK client with email:', email);

  // Will always create a new one. The current email map prevents duplicates
  const factory = getFactory(email);
  // This will create the client for this email. Even though the factory also creates it
  const client = factory.client(email);

  const statefulClient = new StatefulSplitClient(
    env.LOCAL_SPLITS === 'true' ? makeGetTreatmentMockable(client) : client
  );

  statefulSplitClientMap.set(email, statefulClient);
  return statefulClient;
}
